import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Psychology, CheckCircle } from '@mui/icons-material';
import { RecipeV2Detail, AIFinding, AIFindingSeverity, AIFindingStatus } from '../types';
import { UserContext } from '../../../components/shared/useUser';
import useRecipeV2Api from '../hooks/useRecipeV2Api';

interface AIInsightsTabProps {
  recipe: RecipeV2Detail;
}

const severityColor: Record<AIFindingSeverity, 'error' | 'warning' | 'info' | 'default'> = {
  [AIFindingSeverity.Critical]: 'error',
  [AIFindingSeverity.High]: 'error',
  [AIFindingSeverity.Medium]: 'warning',
  [AIFindingSeverity.Low]: 'info',
};

const AIInsightsTab = ({ recipe }: AIInsightsTabProps) => {
  const { t } = useTranslation('recipeV2');
  const { hasPermissionTo } = useContext(UserContext);
  const { dismissAIFinding, acknowledgeAIFinding } = useRecipeV2Api();
  const canDismiss = hasPermissionTo(['RecipeV2AIDismiss']);
  const [findings, setFindings] = useState<AIFinding[]>(recipe.aiFindings);
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [selectedFindingId, setSelectedFindingId] = useState<number | null>(null);
  const [isDismissing, setIsDismissing] = useState(false);
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  const handleDismissClick = (findingId: number) => {
    setSelectedFindingId(findingId);
    setDismissDialogOpen(true);
  };

  const handleDismissConfirm = async () => {
    if (!selectedFindingId) return;
    setIsDismissing(true);
    try {
      await dismissAIFinding(recipe.id, selectedFindingId, dismissReason);
      setFindings((prev) =>
        prev.map((f) =>
          f.id === selectedFindingId
            ? { ...f, status: AIFindingStatus.Dismissed, dismissedReason: dismissReason }
            : f,
        ),
      );
    } catch {
      // lazy: error snackbar would go here in production
    } finally {
      setIsDismissing(false);
      setDismissDialogOpen(false);
      setDismissReason('');
      setSelectedFindingId(null);
    }
  };

  const handleAcknowledge = async (findingId: number) => {
    setIsAcknowledging(true);
    try {
      await acknowledgeAIFinding(recipe.id, findingId);
      setFindings((prev) =>
        prev.map((f) =>
          f.id === findingId ? { ...f, status: AIFindingStatus.Acknowledged } : f,
        ),
      );
    } catch {
      // lazy: error snackbar would go here in production
    } finally {
      setIsAcknowledging(false);
    }
  };

  if (findings.length === 0) {
    return (
      <Stack alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ py: 4, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
        <Typography variant="textXL"
          color="success.main">{t('ai.noFindings')}</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row"
        alignItems="center"
        spacing={1}>
        <Psychology color="action" />
        <Typography variant="textXL">
          {t('ai.title')}
        </Typography>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('ai.severity')}</TableCell>
              <TableCell>{t('ai.finding')}</TableCell>
              <TableCell align="center">{t('ai.confidence')}</TableCell>
              <TableCell>{t('statusLabel')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {findings.map((finding) => (
              <TableRow key={finding.id}>
                <TableCell>
                  <Chip
                    label={t(`ai.${finding.severity.toLowerCase()}`)}
                    size="small"
                    color={severityColor[finding.severity] ?? 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="textSM">{finding.description}</Typography>
                  <Typography variant="textXS"
                    color="text.secondary">
                    {finding.findingType} · {finding.createdAt}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography
                    variant="textSM"
                    fontWeight={600}
                    color={finding.confidence > 0.8 ? 'error.main' : 'text.secondary'}
                  >
                    {(finding.confidence * 100).toFixed(0)}%
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`ai.${finding.status.toLowerCase()}`)}
                    size="small"
                    variant="outlined"
                    color={finding.status === AIFindingStatus.Open ? 'warning' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  {canDismiss && finding.status === AIFindingStatus.Open && (
                    <Stack direction="row"
                      spacing={0.5}>
                      <Button size="md"
                        variant="secondary"
                        onClick={() => handleAcknowledge(finding.id)}
                        disabled={isAcknowledging}>
                        {t('ai.acknowledge')}
                      </Button>
                      <Button size="md"
                        variant="secondary"
                        onClick={() => handleDismissClick(finding.id)}>
                        {t('ai.dismiss')}
                      </Button>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dismiss dialog */}
      <Dialog open={dismissDialogOpen}
        onClose={() => setDismissDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('ai.dismiss')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('ai.dismissReason')}
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissDialogOpen(false)}>Cancel</Button>
          <Button variant="primary"
            onClick={handleDismissConfirm}
            disabled={!dismissReason.trim()}>
            {t('ai.dismiss')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AIInsightsTab;
